// TeamSubteamListModal.jsx
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Box, Typography, Menu, Avatar, CircularProgress, Tooltip, InputBase, Checkbox, Button } from '@mui/material';
import { SearchOutlinedIcon } from 'icons/materialIcons';
import { getTeamMembers } from 'team/apis/teamMemberApi';
import { getTeamSubteams, addMemberToSubteam, removeMemberFromSubteam } from 'team/apis/teamSubteamApi';
import { useTeam } from 'team/contexts/teamContext';
import GroupOutlinedIcon from '@mui/icons-material/GroupOutlined';
import { getSubteamColor, getSubteamInitialsWithDuplicates, getSubteamInitials } from 'team/shared/utils/subteamUtils';
import TeamNotificationSnackbar from 'team/shared/components/notification/TeamNotificationSnackbar';

const TeamSubteamListModal = ({ isOpen, onClose, anchorEl, user, teamId, onSubteamsUpdated, isCardContext = false }) => {
    const { currentTeamId } = useTeam();
    const [allSubteams, setAllSubteams] = useState([]);
    const [memberSubteamIds, setMemberSubteamIds] = useState([]);
    const [selectedSubteamIds, setSelectedSubteamIds] = useState([]);
    const [loadingSubteams, setLoadingSubteams] = useState(false);
    const [isApplying, setIsApplying] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [notification, setNotification] = useState({ open: false, message: '', severity: 'success' });
    const originalSubteamIdsRef = useRef([]);

    // Fetch all subteams and member's associated subteams
    const fetchSubteams = useCallback(async () => {
        if (!teamId) return;

        setLoadingSubteams(true);
        try {
            // Fetch all subteams for the team
            const subteamsResponse = await getTeamSubteams(teamId);
            
            if (subteamsResponse && subteamsResponse.success && subteamsResponse.subteams) {
                setAllSubteams(subteamsResponse.subteams);
            }

            // Fetch member's associated subteams
            if (user?.userId) {
                const membersResponse = await getTeamMembers(teamId, { status: 'active' });
                
                if (membersResponse && membersResponse.members) {
                    const member = membersResponse.members.find(m => {
                        const memberUserId = m.userId || m.user?.userId;
                        return memberUserId === user.userId;
                    });

                    if (member && member.subteamIds && Array.isArray(member.subteamIds)) {
                        // Extract subteam IDs (handle both object and string IDs)
                        const ids = member.subteamIds.map(subteam => {
                            if (subteam && typeof subteam === 'object' && subteam._id) {
                                return subteam._id.toString();
                            }
                            return subteam.toString();
                        });
                        setMemberSubteamIds(ids);
                        setSelectedSubteamIds([...ids]); // Initialize selected with current associations
                        originalSubteamIdsRef.current = [...ids]; // Store original for comparison
                    } else {
                        setMemberSubteamIds([]);
                        setSelectedSubteamIds([]);
                        originalSubteamIdsRef.current = [];
                    }
                }
            }
        } catch (error) {
            console.error('❌ Failed to fetch subteams:', error);
            setAllSubteams([]);
            setMemberSubteamIds([]);
        } finally {
            setLoadingSubteams(false);
        }
    }, [user?.userId, teamId]);

    useEffect(() => {
        if (isOpen) {
            // Reset data when modal opens
            setAllSubteams([]);
            setMemberSubteamIds([]);
            setSelectedSubteamIds([]);
            setSearchQuery('');
            originalSubteamIdsRef.current = [];
            fetchSubteams();
        }
    }, [isOpen, user?.userId, teamId, fetchSubteams]);

    // Filter subteams based on search query (matching subteam section implementation)
    const filteredSubteams = useMemo(() => {
        if (!searchQuery || searchQuery.trim() === '') return allSubteams;
        const query = searchQuery.toLowerCase().trim();
        return allSubteams.filter(subteam => {
            return (
                subteam.name?.toLowerCase().includes(query) ||
                subteam.description?.toLowerCase().includes(query)
            );
        });
    }, [allSubteams, searchQuery]);

    // Check if a subteam is selected (checked)
    const isSubteamSelected = (subteamId) => {
        return selectedSubteamIds.includes(subteamId.toString());
    };

    // Toggle subteam selection
    const handleToggleSubteam = (subteamId) => {
        const subteamIdStr = subteamId.toString();
        setSelectedSubteamIds(prev => {
            if (prev.includes(subteamIdStr)) {
                return prev.filter(id => id !== subteamIdStr);
            } else {
                return [...prev, subteamIdStr];
            }
        });
    };

    // Check if there are changes
    const hasChanges = useMemo(() => {
        const original = originalSubteamIdsRef.current.sort();
        const selected = [...selectedSubteamIds].sort();
        if (original.length !== selected.length) return true;
        return original.some((id, index) => id !== selected[index]);
    }, [selectedSubteamIds]);

    // Handle apply button click
    const handleApply = async () => {
        if (!user?.userId || !teamId || isApplying) return;

        setIsApplying(true);
        try {
            const originalIds = new Set(originalSubteamIdsRef.current);
            const selectedIds = new Set(selectedSubteamIds);

            // Find subteams to add (in selected but not in original)
            const toAdd = selectedSubteamIds.filter(id => !originalIds.has(id));
            // Find subteams to remove (in original but not in selected)
            const toRemove = originalSubteamIdsRef.current.filter(id => !selectedIds.has(id));

            // Add members to new subteams
            for (const subteamId of toAdd) {
                try {
                    await addMemberToSubteam(subteamId, user.userId);
                } catch (error) {
                    console.error(`Error adding member to subteam ${subteamId}:`, error);
                }
            }

            // Remove members from subteams
            for (const subteamId of toRemove) {
                try {
                    await removeMemberFromSubteam(subteamId, user.userId);
                } catch (error) {
                    console.error(`Error removing member from subteam ${subteamId}:`, error);
                }
            }

            // Update state
            setMemberSubteamIds([...selectedSubteamIds]);
            originalSubteamIdsRef.current = [...selectedSubteamIds];

            setNotification({
                open: true,
                message: 'Subteam associations updated successfully',
                severity: 'success'
            });

            // Refresh data
            await fetchSubteams();

            // Notify parent component to refresh
            if (onSubteamsUpdated) {
                onSubteamsUpdated();
            }

            // Close modal after short delay
            setTimeout(() => {
                onClose();
            }, 1000);
        } catch (error) {
            console.error('Error applying subteam changes:', error);
            setNotification({
                open: true,
                message: 'Failed to update subteam associations. Please try again.',
                severity: 'error'
            });
        } finally {
            setIsApplying(false);
        }
    };

    const handleNotificationClose = () => {
        setNotification({ ...notification, open: false });
    };

    // Get member's associated subteams for display in button (use selected for real-time updates)
    const memberSubteams = useMemo(() => {
        const idsToUse = selectedSubteamIds.length > 0 ? selectedSubteamIds : memberSubteamIds;
        return allSubteams.filter(subteam => 
            idsToUse.includes(subteam._id.toString())
        );
    }, [allSubteams, memberSubteamIds, selectedSubteamIds]);

    if (!isOpen) return null;

    // Render overlapping circles for subteams
    const renderSubteamAvatars = () => {
        if (memberSubteams.length === 0) {
            // Show team icon when no subteams
            return (
                <Avatar
                    sx={{
                        width: 25,
                        height: 25,
                        backgroundColor: '#f2f2f2',
                        color: '#828282',
                        border: '2px solid #ffffff',
                    }}
                >
                    <GroupOutlinedIcon sx={{ fontSize: '16px' }} />
                </Avatar>
            );
        }

        // Generate initials with duplicate handling
        const subteamsWithInitials = getSubteamInitialsWithDuplicates(memberSubteams);

        // Show overlapping circles (max 3 visible, rest as +N)
        const maxVisible = 3;
        const visibleSubteams = subteamsWithInitials.slice(0, maxVisible);
        const remainingCount = subteamsWithInitials.length - maxVisible;

        return (
            <Box sx={{ display: 'flex', alignItems: 'center', position: 'relative' }}>
                {visibleSubteams.map((subteam, index) => (
                    <Tooltip
                        key={subteam._id}
                        title={subteam.name}
                        placement="top"
                        arrow
                    >
                        <Avatar
                            src={subteam.logoUrl}
                            sx={{
                                width: 25,
                                height: 25,
                                position: 'relative',
                                zIndex: visibleSubteams.length - index,
                                marginLeft: index > 0 ? '-8px' : 0,
                                border: '2px solid #ffffff',
                                backgroundColor: subteam.logoUrl ? 'transparent' : getSubteamColor(subteam._id, subteam.name),
                                flexShrink: 0,
                                fontSize: subteam.displayInitials?.length > 1 ? '0.65rem' : '0.75rem'
                            }}
                        >
                            {!subteam.logoUrl && subteam.displayInitials}
                        </Avatar>
                    </Tooltip>
                ))}
                {remainingCount > 0 && (
                    <Tooltip
                        title={`${remainingCount} more subteam${remainingCount > 1 ? 's' : ''}`}
                        placement="top"
                        arrow
                    >
                        <Avatar
                            sx={{
                                width: 25,
                                height: 25,
                                position: 'relative',
                                zIndex: 0,
                                marginLeft: '-8px',
                                border: '2px solid #ffffff',
                                backgroundColor: '#e0e0e0',
                                color: '#666',
                                fontSize: '0.7rem',
                                flexShrink: 0
                            }}
                        >
                            +{remainingCount}
                        </Avatar>
                    </Tooltip>
                )}
            </Box>
        );
    };

    return (
        <Menu
            anchorEl={anchorEl}
            open={isOpen}
            onClose={onClose}
            onClick={(event) => event.stopPropagation()}
            anchorOrigin={{
                vertical: isCardContext ? 'bottom' : 'bottom',
                horizontal: 'center',
            }}
            transformOrigin={{
                vertical: isCardContext ? 'top' : 'top',
                horizontal: 'center',
            }}
            PaperProps={{
                sx: {
                    borderRadius: '12px',
                    boxShadow: '0px 8px 24px rgba(0, 0, 0, 0.15)',
                    width: isCardContext ? '280px' : {
                        xs: '70vw',
                        sm: '320px',
                        md: '350px'
                    },
                    maxWidth: isCardContext ? '280px' : '350px',
                    maxHeight: isCardContext ? '350px' : '80vh',
                    overflow: 'hidden',
                    marginTop: isCardContext ? '-8px' : '12px',
                    marginBottom: isCardContext ? '0px' : '0px',
                    padding: isCardContext ? '8px' : '12px',
                    backgroundColor: '#fafafa',
                    ...(isCardContext && {
                        position: 'absolute',
                        zIndex: 1300
                    })
                }
            }}
            MenuListProps={{
                onClick: (event) => event.stopPropagation()
            }}
        >
            {loadingSubteams ? (
                <Box sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: '24px'
                }}>
                    <CircularProgress size={24} />
                    <Typography sx={{ ml: 2 }}>Loading subteams...</Typography>
                </Box>
            ) : (
                <Box>
                    {/* Search Bar */}
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            width: '100%',
                            backgroundColor: 'white',
                            borderRadius: '20px',
                            border: '1px solid #e3e3e3',
                            padding: '8px 16px',
                            marginBottom: '16px',
                            '&:hover': {
                                boxShadow: 2,
                            },
                        }}
                    >
                        <SearchOutlinedIcon
                            sx={{
                                color: '#828282',
                                fontSize: '18px',
                                marginRight: '8px'
                            }}
                        />
                        <InputBase
                            fullWidth
                            value={searchQuery}
                            onChange={(e) => {
                                const newValue = e.target.value;
                                setSearchQuery(newValue);
                            }}
                            onKeyDown={(e) => {
                                e.stopPropagation();
                            }}
                            placeholder="Search..."
                            sx={{
                                fontSize: '0.7rem',
                                color: '#4f4f4f',
                                '& .MuiInputBase-input::placeholder': {
                                    color: '#828282',
                                    opacity: 1,
                                },
                            }}
                        />
                    </Box>

                    {/* Subteams List */}
                    <Box sx={{
                        overflowY: 'auto',
                        maxHeight: isCardContext ? '220px' : '60vh',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '4px',
                        padding: '4px',
                        '&::-webkit-scrollbar': {
                            width: '6px',
                        },
                        '&::-webkit-scrollbar-track': {
                            background: '#f1f1f1',
                            borderRadius: '10px',
                        },
                        '&::-webkit-scrollbar-thumb': {
                            background: '#888',
                            borderRadius: '10px',
                            '&:hover': {
                                background: '#555',
                            }
                        }
                    }}>
                        {filteredSubteams.length === 0 ? (
                            <Box sx={{
                                textAlign: 'center',
                                padding: '24px',
                                color: '#828282'
                            }}>
                                <Typography sx={{ fontSize: '0.875rem' }}>
                                    {searchQuery ? 'No subteams found' : 'No subteams available'}
                                </Typography>
                            </Box>
                        ) : (
                            filteredSubteams.map((subteam) => {
                                const isChecked = isSubteamSelected(subteam._id);
                                return (
                                    <Box
                                        key={subteam._id}
                                        onClick={() => handleToggleSubteam(subteam._id)}
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            padding: '12px',
                                            backgroundColor: '#fff',
                                            borderRadius: '8px',
                                            cursor: 'pointer',
                                            minHeight: '60px',
                                            '&:hover': {
                                                backgroundColor: '#f4f4f4'
                                            }
                                        }}
                                    >
                                        <Avatar
                                            src={subteam.logoUrl}
                                            alt={subteam.name}
                                            sx={{
                                                width: 40,
                                                height: 40,
                                                backgroundColor: subteam.logoUrl ? 'transparent' : getSubteamColor(subteam._id, subteam.name),
                                                border: 'none',
                                                flexShrink: 0,
                                                marginRight: '12px'
                                            }}
                                        >
                                            {!subteam.logoUrl && getSubteamInitials(subteam.name)}
                                        </Avatar>
                                        <Box sx={{
                                            flex: 1,
                                            overflow: 'hidden',
                                            display: 'flex',
                                            flexDirection: 'column',
                                            minWidth: 0, // Important for ellipsis to work
                                            marginRight: '8px'
                                        }}>
                                            <Typography
                                                sx={{
                                                    fontWeight: 400,
                                                    fontSize: '0.875rem',
                                                    color: '#333',
                                                    whiteSpace: 'nowrap',
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis',
                                                    width: '100%'
                                                }}
                                            >
                                                {subteam.name || 'Unnamed Subteam'}
                                            </Typography>
                                            {subteam.description && (
                                                <Typography
                                                    sx={{
                                                        fontSize: '0.8rem',
                                                        color: '#828282',
                                                        marginTop: '2px',
                                                        whiteSpace: 'nowrap',
                                                        overflow: 'hidden',
                                                        textOverflow: 'ellipsis',
                                                        width: '100%'
                                                    }}
                                                >
                                                    {subteam.description}
                                                </Typography>
                                            )}
                                        </Box>
                                        <Checkbox
                                            checked={isChecked}
                                            onChange={() => handleToggleSubteam(subteam._id)}
                                            onClick={(e) => e.stopPropagation()}
                                            sx={{
                                                padding: '4px',
                                                marginLeft: '8px',
                                                flexShrink: 0,
                                                color: '#828282',
                                                '&.Mui-checked': {
                                                    color: '#000',
                                                }
                                            }}
                                        />
                                    </Box>
                                );
                            })
                        )}
                    </Box>

                    {/* Apply Button */}
                    <Box sx={{
                        display: 'flex',
                        justifyContent: 'flex-end',
                        marginTop: '12px',
                        paddingTop: '12px',
                        borderTop: '1px solid #e0e0e0'
                    }}>
                        <Button
                            onClick={(event) => {
                                event.stopPropagation();
                                handleApply();
                            }}
                            disabled={!hasChanges || isApplying}
                            sx={{
                                backgroundColor: hasChanges ? '#000' : '#e0e0e0',
                                color: hasChanges ? '#fff' : '#a6a6a6',
                                borderRadius: '20px',
                                textTransform: 'none',
                                fontSize: '0.7rem',
                                fontWeight: '600',
                                padding: '10px 32px',
                                minWidth: '120px',
                                '&:hover': {
                                    backgroundColor: hasChanges ? '#333' : '#e0e0e0',
                                },
                                '&.Mui-disabled': {
                                    backgroundColor: '#e0e0e0',
                                    color: '#a6a6a6'
                                }
                            }}
                        >
                            {isApplying ? <CircularProgress size={20} sx={{ color: '#fff' }} /> : 'Apply'}
                        </Button>
                    </Box>
                </Box>
            )}

            {/* Notification Snackbar */}
            <TeamNotificationSnackbar
                open={notification.open}
                message={notification.message}
                handleClose={handleNotificationClose}
                severity={notification.severity}
            />
        </Menu>
    );
};

export default TeamSubteamListModal;
